import React, {createContext, useContext, useMemo, useReducer, useState} from "react";
import PropTypes from "prop-types";
import BaseMonthDataLoader from "./Timeline/BaseMonthDataLoader";

const PlannerContext = createContext();

export const usePlanner = () => useContext(PlannerContext);

// Define the default renderResource function outside the component to avoid re-rendering
const defaultGridDataLoader = async () => [];
const defaultEventsLoader = async () => [];
const defaultRenderEvent = () => null;
const defaultRenderDayCell = () => null;
const defaultOnSelect = () => null;

const Planner = (props) => {
    const {
        children,
        minDate,
        maxDate,
        initialDate,
        resources,
        MonthDataLoader=BaseMonthDataLoader,
        eventsLoader=defaultEventsLoader,
        renderEvent=defaultRenderEvent,
        gridDataLoader=defaultGridDataLoader,
        onSelect=defaultOnSelect,
    } = props;

    const [selectedRange, setSelectedRange] = useReducer(
        (oldState, newState) => {
            if(!!newState.from && !!newState.to && (!!newState.resource || !!oldState.resource) ){
                return {...oldState, ...newState}
            }
            else if (!!oldState.resource && !!oldState.from && !!newState.to) {
                return {...oldState, to: newState.to}
            }
            else if (!!oldState.resource && !!oldState.to && !!newState.from) {
                return {...oldState, from: newState.from}
            }
            else {
                return {resource: null, from: null, to: null};
            }
        },
        {
            resource: null,
            from: null,
            to: null
        },
    )
    const [handleClickOutside, setClickOutside] = useState(() => f=>f);  // used by useSelect to reset the selection when the user click somewhere else

    const minDateMem = useMemo(() => new Date(minDate), []);  // eslint-disable-line react-hooks/exhaustive-deps
    const maxDateMem = useMemo(() => new Date(maxDate), []);  // eslint-disable-line react-hooks/exhaustive-deps
    const initialDateMem = useMemo(() => new Date(initialDate), []);  // eslint-disable-line react-hooks/exhaustive-deps

    const [renderDayCell, setRenderDayCell] = useState(defaultRenderDayCell);  // A function that when called cause the planner to re-render the day cell. (used by month vertical)

    // A function that when called cause the planner to scroll to the given date.
    // The function should be set by the child component that implement the scroll.
    const [goToDate, setGoToDate] = useState(() => f=>f);

    const context = {
        minDate: minDateMem,
        maxDate: maxDateMem,
        initialDate: initialDateMem,
        resources,
        selectedRange,
        setSelectedRange,
        goToDate,
        setGoToDate,
        handleClickOutside,
        setClickOutside,
        renderDayCell,
        setRenderDayCell,
        eventsLoader,
        renderEvent,
        gridDataLoader,
        onSelect,
        MonthDataLoader,
    }

    return (
        <PlannerContext.Provider value={context}>
            {children}
        </PlannerContext.Provider>
    )
}

Planner.propTypes = {
    minDate: PropTypes.instanceOf(Date).isRequired,
    maxDate: PropTypes.instanceOf(Date).isRequired,
    initialDate: PropTypes.instanceOf(Date).isRequired,
    eventsLoader: PropTypes.func, // An asynchronous function (startDate:Date, endDate:Date) => [{event}, ...], when this function change the content is re-rendered
    renderEvent: PropTypes.func, // A function (event, segment) => ReactComponent
    /*
     * A resource is a single line of the timeline view, it can be a room, a person, a car, etc.
     * It is an object with the following structure: {
     *  resourceId: str,
     *  renderLabel: () => ReactComponent,
     *  renderDayCell: (date:Date, data:any) => ReactComponent,
     *  hidden: boolean
     * }.
     * The resource are rendered in the given order (this is why we used array and not object, object doesn't
     * preserve the order)
     */
    resources: PropTypes.array,
    gridDataLoader: PropTypes.func, // An asynchronous function (startDate:Date, endDate:Date) => [{resourceId: string, dates: array of dates, data: array of any}], when this function change the grid is re-rendered
    onSelect: PropTypes.func, // A function (from: Date, to: Date, resourceId: string) => void called when a range is selected
}

export default Planner;
